import { useEffect, useState, useCallback } from "react";
import { api, money, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { Users, Eye, ShoppingCart, DollarSign, TrendingUp, MousePointerClick, Loader2, RefreshCw } from "lucide-react";

const RANGES = [
  { key: 7, label: "7D" },
  { key: 30, label: "30D" },
  { key: 90, label: "90D" },
];

const DEVICE_COLORS = { desktop: "#2E90FF", mobile: "#7A6CFF", tablet: "#3ED0C0", other: "#8894A8" };
const fmtDay = (d) => { const [, m, day] = (d || "").split("-"); return m && day ? `${+m}/${+day}` : d; };

const CardTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="border border-white/15 bg-[#070B1C] px-3 py-2 sq-sm text-xs">
      <div className="text-white/50 mb-1">{fmtDay(label)}</div>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-white/70 capitalize">{p.name}:</span>
          <span className="text-white font-bold">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

export const AdminAnalytics = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/analytics?days=${days}`)
      .then((r) => { setData(r.data); setUpdatedAt(new Date()); })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const s = data?.summary;
  const kpis = s ? [
    { label: "Visits", value: s.visits.toLocaleString(), icon: Users, hint: `${s.pageviews.toLocaleString()} pageviews` },
    { label: "Unique Visitors", value: s.unique_visitors.toLocaleString(), icon: Eye, hint: "distinct people" },
    { label: "Add to Cart", value: s.add_to_cart.toLocaleString(), icon: MousePointerClick, hint: `${s.product_views.toLocaleString()} product views` },
    { label: "Orders", value: s.orders.toLocaleString(), icon: ShoppingCart, hint: `${s.begin_checkout.toLocaleString()} checkouts started` },
    { label: "Revenue", value: money(s.revenue), icon: DollarSign, hint: "paid + shipped" },
    { label: "Conversion", value: `${s.conversion_rate}%`, icon: TrendingUp, hint: "orders / visits" },
  ] : [];

  const funnelMax = data?.funnel?.[0]?.value || 1;
  const refMax = Math.max(1, ...(data?.referrers || []).map((r) => r.visits));
  const prodMax = Math.max(1, ...(data?.top_products || []).map((p) => p.views));
  const deviceData = (data?.devices || []).map((d) => ({ name: d.device, value: d.count }));
  const platMax = Math.max(1, ...((data?.platforms) || []).map((p) => p.count));
  const locMax = Math.max(1, ...((data?.locations) || []).map((l) => l.count));

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-[11px] tracking-[0.2em] uppercase text-white/40">Private · visible only to admins · live</p>
          {updatedAt && (
            <p className="text-[10px] text-white/30 mt-1" data-testid="analytics-updated">
              Updated {updatedAt.toLocaleTimeString()} · auto-refreshes every 60s
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-bold border border-white/25 text-white hover:border-electric hover:text-electric transition-colors duration-200 sq-md sq-press inline-flex items-center gap-2"
            data-testid="analytics-refresh"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <div className="flex gap-2" data-testid="analytics-range">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setDays(r.key)}
                className={`px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-bold border transition-colors duration-200 sq-md sq-press ${
                  days === r.key ? "bg-electric text-black border-electric" : "bg-transparent text-white border-white/25 hover:border-electric hover:text-electric"
                }`}
                data-testid={`range-${r.key}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading || !data ? (
        <div className="py-24 flex items-center justify-center text-white/50"><Loader2 className="animate-spin" /></div>
      ) : (
        <div className="space-y-6">
          {/* All-time + today totals (real, cumulative) */}
          {data.totals && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="analytics-totals">
              {[
                { label: "All-time visits", value: data.totals.all_time_visits, tid: "alltime-visits", accent: false },
                { label: "All-time pageviews", value: data.totals.all_time_pageviews, tid: "alltime-pageviews", accent: false },
                { label: "Unique visitors", value: data.totals.all_time_visitors, tid: "alltime-visitors", accent: false },
                { label: "Visits today", value: data.totals.today_visits, tid: "today-visits", accent: true },
              ].map((t) => (
                <div key={t.label} className="border border-white/12 bg-[#0B1024] p-4 sq-lg">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-white/40">{t.label}</div>
                  <div className={`font-display font-bold text-2xl mt-1 ${t.accent ? "text-electric" : "text-white"}`} data-testid={t.tid}>
                    {Number(t.value).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="analytics-kpis">
            {kpis.map((k) => {
              const Icon = k.icon;
              return (
                <div key={k.label} className="border border-white/12 bg-[#0B1024] p-4 sq-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] tracking-[0.2em] uppercase text-white/40">{k.label}</span>
                    <Icon size={15} className="text-electric" />
                  </div>
                  <div className="font-display font-bold text-2xl text-white mt-2">{k.value}</div>
                  <div className="text-[10px] text-white/35 mt-1">{k.hint}</div>
                </div>
              );
            })}
          </div>

          {/* Traffic over time */}
          <div className="border border-white/12 bg-[#0B1024] p-5 sq-lg" data-testid="analytics-timeseries">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/60 mb-4">Traffic over time</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.timeseries} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gVisits" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2E90FF" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#2E90FF" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gVisitors" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#7A6CFF" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#7A6CFF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={24} />
                  <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} width={40} />
                  <Tooltip content={<CardTip />} />
                  <Area type="monotone" dataKey="visits" stroke="#2E90FF" strokeWidth={2} fill="url(#gVisits)" />
                  <Area type="monotone" dataKey="visitors" stroke="#7A6CFF" strokeWidth={2} fill="url(#gVisitors)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex gap-5 mt-3 text-[10px] tracking-[0.15em] uppercase">
              <span className="flex items-center gap-2 text-white/60"><span className="w-2.5 h-2.5 rounded-full bg-electric" /> Visits</span>
              <span className="flex items-center gap-2 text-white/60"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#7A6CFF" }} /> Unique Visitors</span>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Funnel */}
            <div className="border border-white/12 bg-[#0B1024] p-5 sq-lg" data-testid="analytics-funnel">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/60 mb-4">Purchase funnel</div>
              <div className="space-y-3">
                {data.funnel.map((f, i) => {
                  const pct = Math.round((f.value / funnelMax) * 100);
                  const conv = i === 0 ? 100 : Math.round((f.value / funnelMax) * 100);
                  return (
                    <div key={f.step} data-testid={`funnel-${f.step.replace(/\s+/g, "-").toLowerCase()}`}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-white/70">{f.step}</span>
                        <span className="text-white font-bold">{f.value.toLocaleString()} <span className="text-white/35 font-normal">· {conv}%</span></span>
                      </div>
                      <div className="h-3 bg-white/5 sq-sm overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-electric to-[#7A6CFF] sq-sm" style={{ width: `${Math.max(pct, 2)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Devices */}
            <div className="border border-white/12 bg-[#0B1024] p-5 sq-lg" data-testid="analytics-devices">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/60 mb-4">Devices</div>
              {deviceData.length === 0 ? (
                <p className="text-white/40 text-sm py-8 text-center">No data yet</p>
              ) : (
                <div className="flex items-center gap-6">
                  <div className="w-40 h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={deviceData} dataKey="value" nameKey="name" innerRadius={42} outerRadius={68} paddingAngle={2} stroke="none">
                          {deviceData.map((d) => <Cell key={d.name} fill={DEVICE_COLORS[d.name] || DEVICE_COLORS.other} />)}
                        </Pie>
                        <Tooltip content={<CardTip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-2">
                    {deviceData.map((d) => (
                      <div key={d.name} className="flex items-center gap-2 text-sm">
                        <span className="w-3 h-3 rounded-full" style={{ background: DEVICE_COLORS[d.name] || DEVICE_COLORS.other }} />
                        <span className="text-white/70 capitalize">{d.name}</span>
                        <span className="text-white font-bold ml-2">{d.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Platforms / devices */}
            <div className="border border-white/12 bg-[#0B1024] p-5 sq-lg" data-testid="analytics-platforms">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/60 mb-4">Platforms</div>
              {(data.platforms || []).length === 0 ? (
                <p className="text-white/40 text-sm py-8 text-center">No data yet</p>
              ) : (
                <div className="space-y-3">
                  {data.platforms.map((p) => (
                    <div key={p.platform} data-testid={`platform-${p.platform.toLowerCase().replace(/\s+/g, "-")}`}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-white/80">{p.platform}</span>
                        <span className="text-white/50 shrink-0">{p.count}</span>
                      </div>
                      <div className="h-2 bg-white/5 sq-sm overflow-hidden">
                        <div className="h-full bg-electric sq-sm" style={{ width: `${Math.max((p.count / platMax) * 100, 3)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top locations */}
            <div className="border border-white/12 bg-[#0B1024] p-5 sq-lg" data-testid="analytics-locations">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/60 mb-4">Top locations</div>
              {(data.locations || []).length === 0 ? (
                <p className="text-white/40 text-sm py-8 text-center">No data yet</p>
              ) : (
                <div className="space-y-3">
                  {data.locations.map((l) => (
                    <div key={l.country} data-testid={`location-${(l.country || "unknown").toLowerCase().replace(/\s+/g, "-")}`}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-white/80 truncate pr-2">{l.country}</span>
                        <span className="text-white/50 shrink-0">{l.count}</span>
                      </div>
                      <div className="h-2 bg-white/5 sq-sm overflow-hidden">
                        <div className="h-full bg-[#7A6CFF] sq-sm" style={{ width: `${Math.max((l.count / locMax) * 100, 3)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top products */}
            <div className="border border-white/12 bg-[#0B1024] p-5 sq-lg" data-testid="analytics-top-products">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/60 mb-4">Top products</div>
              {data.top_products.length === 0 ? (
                <p className="text-white/40 text-sm py-8 text-center">No product views yet</p>
              ) : (
                <div className="space-y-3">
                  {data.top_products.map((p) => (
                    <div key={p.slug}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-white/80 truncate pr-2">{p.name}</span>
                        <span className="text-white/50 shrink-0">{p.views} views · {p.add_to_carts} carts</span>
                      </div>
                      <div className="h-2 bg-white/5 sq-sm overflow-hidden">
                        <div className="h-full bg-electric sq-sm" style={{ width: `${Math.max((p.views / prodMax) * 100, 3)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Traffic sources */}
            <div className="border border-white/12 bg-[#0B1024] p-5 sq-lg" data-testid="analytics-referrers">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/60 mb-4">Traffic sources</div>
              {data.referrers.length === 0 ? (
                <p className="text-white/40 text-sm py-8 text-center">No data yet</p>
              ) : (
                <div className="space-y-3">
                  {data.referrers.map((r) => (
                    <div key={r.source}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-white/80 truncate pr-2">{r.source}</span>
                        <span className="text-white/50 shrink-0">{r.visits}</span>
                      </div>
                      <div className="h-2 bg-white/5 sq-sm overflow-hidden">
                        <div className="h-full bg-[#7A6CFF] sq-sm" style={{ width: `${Math.max((r.visits / refMax) * 100, 3)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
